import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Loader2, Play, Sparkles, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { getVoicePrefs, loadVoices, pickVoice, rankVoices, setVoicePrefs, speakWithDevice, stopDeviceSpeech, voiceLabel, voiceSupported } from "@/lib/deviceVoice";

const SAMPLE = "Hi, I'm your copilot. Say things like: take me to stock, or how did we do today?";

/**
 * Session 32/33 — AI Copilot settings (Owner dashboard → Settings → AI).
 *  • Speak replies on/off (per user) · hands-free on open
 *  • Device voice picker (free, stored on this device) with speed / pitch
 *  • OpenAI voice picker only when the server reports it is actually usable
 *  • This month's usage against the business cap (read-only; the cap is set
 *    by NFC Solutions admin per business)
 */
export const AiSettingsPanel = ({ http, authHeaders, isOwner, onSettingsChanged }) => {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deviceVoices, setDeviceVoices] = useState([]);
  const [prefs, setPrefs] = useState(() => getVoicePrefs());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await http.get("/ai/context", { headers: authHeaders });
      setCtx(r.data || null);
    } catch (_) {
      setCtx(null);
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let alive = true;
    loadVoices().then((v) => { if (alive) setDeviceVoices(rankVoices(v)); });
    const onChange = () => loadVoices().then((v) => { if (alive) setDeviceVoices(rankVoices(v)); });
    if (voiceSupported()) { try { window.speechSynthesis.addEventListener("voiceschanged", onChange); } catch (_) { /* noop */ } }
    return () => { alive = false; if (voiceSupported()) { try { window.speechSynthesis.removeEventListener("voiceschanged", onChange); } catch (_) { /* noop */ } } stopDeviceSpeech(); };
  }, []);

  const settings = ctx?.settings || {};
  const voices = ctx?.voices || [];
  const usage = ctx?.usage || null;
  const apiBase = useMemo(() => (http?.defaults?.baseURL || "").replace(/\/$/, ""), [http]);
  const openAiUsable = settings.tts_available === true;
  const chosenDevice = useMemo(() => pickVoice(deviceVoices, prefs.voiceURI), [deviceVoices, prefs.voiceURI]);
  const chosenValue = chosenDevice ? chosenDevice.voiceURI : "__default";

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await http.put("/ai/settings", patch, { headers: authHeaders });
      setCtx((c) => ({ ...(c || {}), settings: { ...(c?.settings || {}), ...(r.data || {}) } }));
      onSettingsChanged && onSettingsChanged(r.data);
      toast.success("AI settings saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save AI settings.");
    } finally {
      setSaving(false);
    }
  };

  const updatePrefs = (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setVoicePrefs(patch);
  };

  const previewDevice = async (voice) => {
    setTesting(true);
    const ok = await speakWithDevice(SAMPLE, { voice: voice || chosenDevice || undefined });
    if (!ok) toast.info("This device has no spoken voice available.");
    setTesting(false);
  };

  const previewOpenAi = async (voiceId) => {
    setTesting(true);
    try {
      const res = await fetch(`${apiBase}/ai/tts`, { method: "POST", headers: { "Content-Type": "application/json", ...(authHeaders || {}) }, body: JSON.stringify({ text: SAMPLE, voice: voiceId }) });
      if (res.ok) {
        const url = URL.createObjectURL(await res.blob());
        const audio = new Audio(url);
        audio.onended = () => { URL.revokeObjectURL(url); setTesting(false); };
        audio.onerror = () => { URL.revokeObjectURL(url); setTesting(false); };
        await audio.play();
        return;
      }
      // Quietly fall back — and refresh so the OpenAI picker hides itself.
      await speakWithDevice(SAMPLE);
      load();
    } catch (_) {
      await speakWithDevice(SAMPLE);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="rounded-2xl border border-border p-6 text-sm text-muted-foreground" data-testid="ai-settings-loading"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Loading AI settings…</div>;
  }
  if (!ctx?.enabled) {
    return (
      <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="ai-settings-disabled">
        The AI copilot is switched off for this business. Ask NFC Solutions to enable it.
      </div>
    );
  }
  if (!ctx?.allowed) {
    return <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="ai-settings-not-allowed">You don't have the AI Assistant permission. The owner can grant it under Staff &amp; Access.</div>;
  }

  const usedZar = Number(usage?.used_zar || 0);
  const capZar = Number(usage?.cap_zar || 0);
  const pct = capZar > 0 ? Math.min(100, Math.round((usedZar / capZar) * 100)) : 0;
  const effective = settings.effective_voice;

  return (
    <div className="space-y-6" data-testid="ai-settings-panel">
      {/* usage */}
      <section className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">This month's AI usage</p>
            <p className="text-xs text-muted-foreground">Brain: {ctx.model} · {usage?.requests || 0} requests · {((usage?.input_tokens || 0) + (usage?.output_tokens || 0)).toLocaleString()} tokens{usage?.tts_requests ? ` · ${usage.tts_requests} spoken replies` : ""}</p>
          </div>
          <p className="text-lg font-semibold tabular-nums" data-testid="ai-settings-usage">R{usedZar.toFixed(2)}{capZar > 0 ? <span className="text-sm font-normal text-muted-foreground"> / R{capZar.toFixed(0)}</span> : null}</p>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary"><div className={`h-full ${pct >= 90 ? "bg-destructive" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} /></div>
        <p className="mt-2 text-xs text-muted-foreground">{capZar > 0 ? `The assistant pauses when the monthly budget of R${capZar.toFixed(0)} is reached. Your NFC Solutions administrator sets this budget.` : "No monthly budget is set for this business."}</p>
      </section>

      {/* speech */}
      <section className="rounded-2xl border border-border bg-card p-4 space-y-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-semibold">Speak replies aloud</Label>
            <p className="text-xs text-muted-foreground">Turn off if you only want to read the answers.</p>
          </div>
          <Switch checked={settings.speak_replies !== false} disabled={saving} onCheckedChange={(v) => save({ speak_replies: v })} data-testid="ai-settings-speak-toggle" />
        </div>
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-semibold">Hands-free when the copilot opens</Label>
            <p className="text-xs text-muted-foreground">Starts listening straight away, so you can talk and hide the chat.</p>
          </div>
          <Switch checked={settings.auto_listen === true} disabled={saving} onCheckedChange={(v) => save({ auto_listen: v })} data-testid="ai-settings-autolisten-toggle" />
        </div>

        {/* device voice */}
        <div className="space-y-3" data-testid="ai-device-voice">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm font-semibold">Voice on this device</Label>
            <Badge variant="outline" className="text-[10px]" data-testid="ai-settings-voice-source">{openAiUsable ? "OpenAI voice" : "Device voice"}</Badge>
          </div>
          <p className="text-xs text-muted-foreground">Free, built into your phone or computer. The list differs per device, so this choice is saved here only. Voices marked “natural” sound best.</p>
          {voiceSupported() ? (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Select value={chosenValue} onValueChange={(v) => updatePrefs({ voiceURI: v === "__default" ? null : v })}>
                  <SelectTrigger className="h-11 flex-1 rounded-xl" data-testid="ai-device-voice-select">
                    <SelectValue placeholder="Choose a voice" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {deviceVoices.length === 0 ? <SelectItem value="__default">Device default</SelectItem> : null}
                    {deviceVoices.map((v, i) => (
                      <SelectItem key={v.voiceURI || v.name} value={v.voiceURI || v.name} data-testid={`ai-device-voice-option-${i}`}>
                        {voiceLabel(v)}{i === 0 ? " · recommended" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" className="h-11 rounded-xl" disabled={testing} onClick={() => previewDevice()} data-testid="ai-device-voice-preview">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Preview
                </Button>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="flex items-center justify-between text-xs"><Label className="text-xs">Speed</Label><span className="tabular-nums text-muted-foreground" data-testid="ai-device-voice-rate">{prefs.rate.toFixed(2)}×</span></div>
                  <Slider className="mt-2" min={0.7} max={1.3} step={0.05} value={[prefs.rate]} onValueChange={([v]) => updatePrefs({ rate: v })} data-testid="ai-device-voice-rate-slider" />
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs"><Label className="text-xs">Pitch</Label><span className="tabular-nums text-muted-foreground" data-testid="ai-device-voice-pitch">{prefs.pitch.toFixed(2)}</span></div>
                  <Slider className="mt-2" min={0.8} max={1.2} step={0.05} value={[prefs.pitch]} onValueChange={([v]) => updatePrefs({ pitch: v })} data-testid="ai-device-voice-pitch-slider" />
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">This browser cannot speak. Replies will show as text only.</p>
          )}
        </div>

        {/* OpenAI voice — only when the server says it is actually usable */}
        {openAiUsable ? (
          <div data-testid="ai-openai-voice">
            <Label className="text-sm font-semibold">OpenAI voice</Label>
            <p className="mb-2 text-xs text-muted-foreground">Pick the voice for this business. Tap play to hear a sample.</p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {voices.map((v) => {
                const active = effective === v.id;
                return (
                  <div key={v.id} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/8" : "border-border"}`} data-testid={`ai-voice-${v.id}`}>
                    <button type="button" className="flex-1 text-left" disabled={saving} onClick={() => save({ voice: v.id })} data-testid={`ai-voice-pick-${v.id}`}>
                      <p className="text-sm font-medium">{v.label}{active ? <Check className="ml-1 inline h-3.5 w-3.5 text-[hsl(var(--primary))]" /> : null}</p>
                      <p className="text-[11px] text-muted-foreground">{v.hint}</p>
                    </button>
                    <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg" disabled={testing} onClick={() => previewOpenAi(v.id)} aria-label={`Preview ${v.label}`} data-testid={`ai-voice-test-${v.id}`}>
                      {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    </Button>
                  </div>
                );
              })}
            </div>
            {!isOwner ? <p className="mt-2 text-[11px] text-muted-foreground">Only the owner's choice changes the business default; yours applies to you.</p> : null}
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-border bg-card p-4">
        <p className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4" /> What the copilot can do</p>
        <ul className="mt-2 grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          <li>Open any screen: “take me to cash management”</li>
          <li>Answer numbers: “how did we do this week vs last week?”</li>
          <li>Stock: “what's low on stock?”, “add 12 tonics”</li>
          <li>Prices: “change the lemonade to R25” — always confirmed first</li>
          <li>Stock take by voice: open the stock take, then dictate counts</li>
          <li>Hands-free: turn it on, hide the chat, and just talk</li>
        </ul>
        <p className="mt-2 flex items-center gap-1 text-[11px] text-muted-foreground"><Volume2 className="h-3 w-3" /> Every action is written to the audit log under Reports.</p>
      </section>
    </div>
  );
};
